import type { Resource } from "@medplum/fhirtypes";
import {
  ALLERGY_INTOLERANCE_ENTITY_ID_SYSTEM,
  CARE_PLAN_ENTITY_ID_SYSTEM,
  CARE_PLAN_GOAL_MEMBER_ID_SYSTEM,
  CASE_ENTITY_ID_SYSTEM,
  CLINICAL_NOTE_ENTITY_ID_SYSTEM,
  CONDITION_ENTITY_ID_SYSTEM,
  CONSENT_ENTITY_ID_SYSTEM,
  DEVICE_DEFINITION_LINEAGE_SYSTEM,
  DEVICE_DEFINITION_REVISION_SYSTEM,
  DEVICE_ENTITY_ID_SYSTEM,
  DIAGNOSTIC_PROCEDURE_ENTITY_ID_SYSTEM,
  DIAGNOSTIC_TRIAL_ID_SYSTEM,
  EXAM_ENTITY_ID_SYSTEM,
  FINDING_DEFINITION_LINEAGE_SYSTEM,
  FINDING_DEFINITION_REVISION_SYSTEM,
  FINDING_ENTITY_ID_SYSTEM,
  FLAG_ENTITY_ID_SYSTEM,
  FLOW_INSTANCE_ENTITY_ID_SYSTEM,
  HEALTHCARE_SERVICE_ENTITY_ID_SYSTEM,
  LOCATION_ENTITY_ID_SYSTEM,
  MEDICATION_STATEMENT_ENTITY_ID_SYSTEM,
  OBSERVATION_ENTITY_ID_SYSTEM,
  ORDER_SET_ENTITY_ID_SYSTEM,
  OUTBOUND_MESSAGE_ENTITY_ID_SYSTEM,
  PATIENT_DOCUMENT_ENTITY_ID_SYSTEM,
  PATIENT_ENTITY_ID_SYSTEM,
  PERFORMANCE_MEASURE_ENTITY_ID_SYSTEM,
  PRACTITIONER_ENTITY_ID_SYSTEM,
  QUESTIONNAIRE_INSTANCE_ENTITY_ID_SYSTEM,
  RELATED_PERSON_ENTITY_ID_SYSTEM,
  SCHEDULE_BLOCK_ID_SYSTEM,
  SCHEDULE_ENTITY_ID_SYSTEM,
  TASK_ENTITY_ID_SYSTEM,
  TREATMENT_OBSERVATION_ID_SYSTEM,
  TREATMENT_PROCEDURE_ENTITY_ID_SYSTEM,
  TREATMENT_TRIAL_ID_SYSTEM,
  TREATMENT_UNCAPTURED_ID_SYSTEM,
  VISIT_ASSIGNMENT_MEMBER_ID_SYSTEM,
  VISIT_ENTITY_ID_SYSTEM,
  VISIT_EXECUTION_MEMBER_ID_SYSTEM,
} from "@repo/fhir-systems/identifiers";
import { ORDER_ID_URL } from "./engine/mappers/order-set.mapper";
import { DIAGNOSTIC_OBSERVATION_ID_SYSTEM, DIAGNOSTIC_UNCAPTURED_ID_SYSTEM } from "./lib/diagnostic-procedure-fhir";

/**
 * The key a synced row is stored under: the identity its mapper writes, so a
 * row a client writes optimistically and the row the server returns share it.
 * Read from the resource alone — `deriveFields` never runs on an optimistic
 * insert — and from `identifier`s, never from a literal `Type/id`.
 */

type Identified = { system?: string; value?: string };
/** `identifier` is a list on most types and a single Identifier on a few (QuestionnaireResponse). */
type Row = Record<string, unknown> & { identifier?: Identified[] | Identified };

/** Where a child finds its parent: its own identifier, or the identifier on a reference field. */
export type ParentSource =
  | { readonly identifier: readonly string[] }
  | { readonly reference: string; readonly systems: readonly string[] };

export type MemberSource =
  | { readonly identifier: string }
  | { readonly extension: string }
  | { readonly constant: string };

export type KeyRule =
  | { readonly kind: "entity"; readonly system: string }
  | { readonly kind: "member"; readonly parent: ParentSource; readonly member: MemberSource }
  /** One identifier holding `parentId|memberId`. */
  | { readonly kind: "scoped"; readonly system: string }
  | { readonly kind: "canonical" }
  | { readonly kind: "revision"; readonly lineage: string; readonly revision: string };

const entity = (system: string): KeyRule => ({ kind: "entity", system });
const procedureParent: ParentSource = {
  reference: "partOf",
  systems: [DIAGNOSTIC_PROCEDURE_ENTITY_ID_SYSTEM, TREATMENT_PROCEDURE_ENTITY_ID_SYSTEM],
};
const procedureMember = (system: string): KeyRule => ({
  kind: "member",
  parent: procedureParent,
  member: { identifier: system },
});

/** Rules per resource type, first match wins: entity identity before any member reading. */
export const KEY_RULES: Readonly<Record<string, readonly KeyRule[]>> = {
  ActivityDefinition: [{ kind: "canonical" }],
  AllergyIntolerance: [entity(ALLERGY_INTOLERANCE_ENTITY_ID_SYSTEM)],
  Appointment: [entity(VISIT_ENTITY_ID_SYSTEM)],
  CarePlan: [entity(CARE_PLAN_ENTITY_ID_SYSTEM)],
  ClinicalImpression: [entity(CLINICAL_NOTE_ENTITY_ID_SYSTEM)],
  Communication: [entity(OUTBOUND_MESSAGE_ENTITY_ID_SYSTEM)],
  Condition: [entity(CONDITION_ENTITY_ID_SYSTEM)],
  Consent: [entity(CONSENT_ENTITY_ID_SYSTEM)],
  Device: [entity(DEVICE_ENTITY_ID_SYSTEM)],
  DeviceDefinition: [
    { kind: "revision", lineage: DEVICE_DEFINITION_LINEAGE_SYSTEM, revision: DEVICE_DEFINITION_REVISION_SYSTEM },
  ],
  DocumentReference: [entity(PATIENT_DOCUMENT_ENTITY_ID_SYSTEM)],
  Encounter: [
    {
      kind: "member",
      parent: { reference: "appointment", systems: [VISIT_ENTITY_ID_SYSTEM] },
      member: { identifier: VISIT_EXECUTION_MEMBER_ID_SYSTEM },
    },
  ],
  EpisodeOfCare: [entity(CASE_ENTITY_ID_SYSTEM)],
  Flag: [entity(FLAG_ENTITY_ID_SYSTEM)],
  Goal: [
    {
      kind: "member",
      parent: { identifier: [CARE_PLAN_ENTITY_ID_SYSTEM] },
      member: { identifier: CARE_PLAN_GOAL_MEMBER_ID_SYSTEM },
    },
  ],
  HealthcareService: [entity(HEALTHCARE_SERVICE_ENTITY_ID_SYSTEM)],
  Location: [entity(LOCATION_ENTITY_ID_SYSTEM)],
  MedicationStatement: [entity(MEDICATION_STATEMENT_ENTITY_ID_SYSTEM)],
  ObservationDefinition: [
    { kind: "revision", lineage: FINDING_DEFINITION_LINEAGE_SYSTEM, revision: FINDING_DEFINITION_REVISION_SYSTEM },
  ],
  Observation: [
    entity(EXAM_ENTITY_ID_SYSTEM),
    entity(FINDING_ENTITY_ID_SYSTEM),
    entity(OBSERVATION_ENTITY_ID_SYSTEM),
    procedureMember(DIAGNOSTIC_OBSERVATION_ID_SYSTEM),
    procedureMember(DIAGNOSTIC_TRIAL_ID_SYSTEM),
    procedureMember(DIAGNOSTIC_UNCAPTURED_ID_SYSTEM),
    procedureMember(TREATMENT_OBSERVATION_ID_SYSTEM),
    procedureMember(TREATMENT_TRIAL_ID_SYSTEM),
    procedureMember(TREATMENT_UNCAPTURED_ID_SYSTEM),
  ],
  Patient: [entity(PATIENT_ENTITY_ID_SYSTEM)],
  RelatedPerson: [entity(RELATED_PERSON_ENTITY_ID_SYSTEM)],
  PlanDefinition: [{ kind: "canonical" }],
  Practitioner: [entity(PRACTITIONER_ENTITY_ID_SYSTEM)],
  PractitionerRole: [
    {
      kind: "member",
      parent: { reference: "practitioner", systems: [PRACTITIONER_ENTITY_ID_SYSTEM] },
      member: { constant: "role" },
    },
  ],
  Procedure: [entity(DIAGNOSTIC_PROCEDURE_ENTITY_ID_SYSTEM), entity(TREATMENT_PROCEDURE_ENTITY_ID_SYSTEM)],
  Questionnaire: [{ kind: "canonical" }],
  QuestionnaireResponse: [
    entity(QUESTIONNAIRE_INSTANCE_ENTITY_ID_SYSTEM),
    entity(PERFORMANCE_MEASURE_ENTITY_ID_SYSTEM),
  ],
  RequestGroup: [entity(ORDER_SET_ENTITY_ID_SYSTEM), entity(FLOW_INSTANCE_ENTITY_ID_SYSTEM)],
  Schedule: [entity(SCHEDULE_ENTITY_ID_SYSTEM)],
  ServiceRequest: [
    {
      kind: "member",
      parent: { reference: "basedOn", systems: [ORDER_SET_ENTITY_ID_SYSTEM] },
      member: { extension: ORDER_ID_URL },
    },
  ],
  Slot: [
    {
      kind: "member",
      parent: { identifier: [VISIT_ENTITY_ID_SYSTEM] },
      member: { identifier: VISIT_ASSIGNMENT_MEMBER_ID_SYSTEM },
    },
    { kind: "scoped", system: SCHEDULE_BLOCK_ID_SYSTEM },
  ],
  Task: [entity(TASK_ENTITY_ID_SYSTEM)],
};

/** A child's key. Member ids are unique only inside their parent. */
export function memberKey(parentId: string, memberId: string): string {
  return `${parentId}/${memberId}`;
}

/** The one populated value under `system`; two is ambiguous, and ambiguity is no identity. */
function only(
  identifiers: readonly Identified[] | Identified | undefined,
  systems: readonly string[],
): string | undefined {
  const list = Array.isArray(identifiers) ? identifiers : identifiers ? [identifiers] : [];
  const values = list.flatMap((identifier) =>
    identifier.system && systems.includes(identifier.system) && identifier.value ? [identifier.value] : [],
  );
  return values.length === 1 ? values[0] : undefined;
}

function referenceIdentifiers(row: Row, field: string): Identified[] {
  const value = row[field];
  const references = (Array.isArray(value) ? value : value ? [value] : []) as Array<{ identifier?: Identified }>;
  return references.flatMap((reference) => (reference.identifier ? [reference.identifier] : []));
}

function parentOf(row: Row, source: ParentSource): string | undefined {
  return "identifier" in source
    ? only(row.identifier, source.identifier)
    : only(referenceIdentifiers(row, source.reference), source.systems);
}

function memberOf(row: Row, source: MemberSource): string | undefined {
  if ("constant" in source) return source.constant;
  if ("identifier" in source) return only(row.identifier, [source.identifier]);
  const extensions = (row.extension ?? []) as Array<{ url?: string; valueString?: string }>;
  const values = extensions.flatMap((extension) =>
    extension.url === source.extension && extension.valueString ? [extension.valueString] : [],
  );
  return values.length === 1 ? values[0] : undefined;
}

function apply(rule: KeyRule, row: Row): string | undefined {
  switch (rule.kind) {
    case "entity":
      return only(row.identifier, [rule.system]);
    case "member": {
      const member = memberOf(row, rule.member);
      if (!member) return undefined;
      const parent = parentOf(row, rule.parent);
      return parent ? memberKey(parent, member) : undefined;
    }
    case "scoped": {
      const value = only(row.identifier, [rule.system]);
      const split = value?.indexOf("|") ?? -1;
      if (!value || split <= 0 || split === value.length - 1) return undefined;
      return memberKey(value.slice(0, split), value.slice(split + 1));
    }
    case "canonical":
      return typeof row.url === "string" && typeof row.version === "string" && row.url && row.version
        ? `${row.url}|${row.version}`
        : undefined;
    case "revision": {
      const lineage = only(row.identifier, [rule.lineage]);
      const revision = only(row.identifier, [rule.revision]);
      return lineage && revision ? `${lineage}|${revision}` : undefined;
    }
  }
}

/** The identity a resource's mapper writes on it, or undefined when it carries none. */
export function resourceKey(resource: Resource): string | undefined {
  const row = resource as unknown as Row;
  for (const rule of KEY_RULES[resource.resourceType] ?? []) {
    const key = apply(rule, row);
    if (key) return key;
  }
  return undefined;
}

/**
 * The key a row is stored under. A row with no domain identity is one the
 * client never creates (a change-log entry, a lock), so its FHIR id is the only
 * identity it has.
 */
export function syncKey(resource: Resource): string {
  return resourceKey(resource) ?? resource.id ?? "";
}
